import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, MessageSquare, Plus, X, Search } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import LoginPrompt from "../components/LoginPrompt";

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days}d`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function Avatar({ picture, name, size = 40 }) {
  const initial = (name || "?")[0]?.toUpperCase() || "?";
  if (picture) {
    return (
      <img
        src={picture}
        alt=""
        style={{ width: size, height: size }}
        className="rounded-full ring-1 ring-border flex-shrink-0"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      className="rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-foreground flex-shrink-0"
    >
      {initial}
    </div>
  );
}

export default function Messages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { handleOrId } = useParams();

  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const threadEndRef = useRef(null);

  // Compose ("New message") state — toggled from the conversations header.
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeQuery, setComposeQuery] = useState("");
  const [composeResults, setComposeResults] = useState([]);
  const [composeSearching, setComposeSearching] = useState(false);

  const fetchConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/messages/conversations");
      setConversations(data?.items || []);
    } catch (err) {
      console.error("conversations fetch failed", err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchThread = useCallback(async (slug) => {
    if (!slug) { setThread(null); return; }
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/messages/with/${encodeURIComponent(slug)}`);
      setThread(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Reader not found.");
        navigate("/messages");
      } else {
        console.error("thread fetch failed", err);
        toast.error("Could not open the thread.");
      }
    } finally {
      setThreadLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    fetchConversations();
  }, [user, fetchConversations]);

  useEffect(() => {
    if (!user) return;
    fetchThread(handleOrId);
    setDraft("");
  }, [user, handleOrId, fetchThread]);

  // Auto-scroll to newest message when the thread updates.
  useEffect(() => {
    if (!thread) return;
    const el = threadEndRef.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread]);

  // Debounced user search for the Compose panel.
  useEffect(() => {
    if (!composeOpen) { setComposeResults([]); return; }
    const q = composeQuery.trim();
    if (q.length < 2) { setComposeResults([]); return; }
    let cancelled = false;
    setComposeSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/search", { params: { q } });
        if (!cancelled) setComposeResults(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setComposeResults([]);
      } finally {
        if (!cancelled) setComposeSearching(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [composeQuery, composeOpen]);

  const openCompose = () => {
    setComposeOpen(true);
    setComposeQuery("");
    setComposeResults([]);
  };

  const startConversationWith = (target) => {
    const slug = target?.handle || target?.user_id;
    if (!slug) return;
    setComposeOpen(false);
    setComposeQuery("");
    setComposeResults([]);
    navigate(`/messages/${slug}`);
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const content = draft.trim();
    if (!content || !thread?.other) return;
    if (content.length > 2000) {
      toast.error("Message is over the 2,000 character limit.");
      return;
    }
    setSending(true);
    const recipient = thread.other.handle || thread.other.user_id;
    // Optimistic append.
    const tempId = `tmp_${Math.random().toString(36).slice(2)}`;
    const tempMsg = {
      id: tempId,
      content,
      from_user_id: user.user_id,
      to_user_id: thread.other.user_id,
      created_at: new Date().toISOString(),
      mine: true,
      read: false,
      pending: true,
    };
    setThread((cur) => cur ? { ...cur, messages: [...(cur.messages || []), tempMsg] } : cur);
    setDraft("");
    try {
      const { data } = await api.post(`/messages/${encodeURIComponent(recipient)}`, { content });
      const real = data?.message;
      setThread((cur) => {
        if (!cur) return cur;
        const without = (cur.messages || []).filter((m) => m.id !== tempId);
        return {
          ...cur,
          messages: real ? [...without, { ...real, mine: true }] : without,
        };
      });
      // Refresh the conversations list silently so the preview updates.
      fetchConversations();
    } catch (err) {
      // Rollback
      setThread((cur) => cur ? { ...cur, messages: (cur.messages || []).filter((m) => m.id !== tempId) } : cur);
      setDraft(content);
      if (err?.response?.status === 403) {
        toast.error("This reader isn't accepting direct messages.");
      } else {
        toast.error("Could not send.");
      }
    } finally {
      setSending(false);
    }
  };

  const showThreadOnMobile = useMemo(() => !!handleOrId, [handleOrId]);

  if (loading) return null;
  if (!user) {
    return (
      <LoginPrompt
        title="Private letters await."
        blurb="Sign in to start direct conversations with other seekers — and to receive the ones already waiting for you."
      />
    );
  }

  return (
    <div data-testid="messages-page" className="max-w-6xl mx-auto px-4 sm:px-10 py-12 sm:py-20">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="hidden sm:block">
          <div className="label mb-2 text-[hsl(var(--accent))]">Direct messages</div>
          <h1 className="serif text-5xl text-foreground leading-none">Private letters</h1>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            One-to-one conversations with other readers. Quiet, simple, private.
          </p>
        </div>
        <button
          type="button"
          data-testid="header-compose-cta"
          onClick={openCompose}
          className="btn-gold inline-flex items-center gap-2 ml-auto"
        >
          <Plus size={14} strokeWidth={2.25} />
          New message
        </button>
      </div>

      <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)] gap-px bg-border min-h-[64vh]">
        {/* CONVERSATION LIST */}
        <aside
          data-testid="conversations-list"
          className={`bg-card ${showThreadOnMobile ? "hidden md:block" : "block"}`}
        >
          <div className="hairline-b px-5 py-3 flex items-center justify-between">
            <span className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Conversations
            </span>
            <button
              type="button"
              data-testid="compose-toggle"
              onClick={composeOpen ? () => setComposeOpen(false) : openCompose}
              className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--primary))] hover:opacity-80"
              aria-label={composeOpen ? "Close composer" : "New message"}
            >
              {composeOpen ? <><X size={12} /> Cancel</> : <><Plus size={12} /> New</>}
            </button>
          </div>

          {/* COMPOSE PANEL */}
          {composeOpen && (
            <div data-testid="compose-panel" className="hairline-b px-5 py-4 bg-[hsl(var(--accent)/0.04)]">
              <label className="block label mb-2">To</label>
              <div className="flex items-center gap-2 border-b border-border focus-within:border-[hsl(var(--primary))]">
                <Search size={14} className="text-muted-foreground/70" />
                <input
                  data-testid="compose-search-input"
                  autoFocus
                  type="text"
                  value={composeQuery}
                  onChange={(e) => setComposeQuery(e.target.value)}
                  placeholder="Search by @handle or name…"
                  className="flex-1 bg-transparent py-2 text-[14px] focus:outline-none placeholder:text-muted-foreground/60"
                />
              </div>
              {composeQuery.trim().length < 2 ? (
                <p className="mt-3 text-[12px] text-muted-foreground italic serif">
                  Type at least 2 characters to search readers.
                </p>
              ) : composeSearching ? (
                <p className="mt-3 text-[12px] text-muted-foreground italic serif">Searching…</p>
              ) : composeResults.length === 0 ? (
                <p
                  data-testid="compose-no-results"
                  className="mt-3 text-[12px] text-muted-foreground italic serif"
                >
                  No reader found. Try a different name or @handle.
                </p>
              ) : (
                <ul data-testid="compose-results" className="mt-3 space-y-1">
                  {composeResults.map((r) => {
                    const slug = r.handle || r.user_id;
                    return (
                      <li key={r.user_id}>
                        <button
                          type="button"
                          data-testid={`compose-result-${slug}`}
                          onClick={() => startConversationWith(r)}
                          disabled={r.allow_dms === false}
                          className="w-full flex items-center gap-3 px-2 py-2 text-left transition-colors hover:bg-[hsl(var(--primary)/0.06)] disabled:opacity-50 disabled:cursor-not-allowed"
                          title={r.allow_dms === false ? "This reader isn't accepting messages" : "Start a conversation"}
                        >
                          <Avatar picture={r.picture} name={r.name} size={32} />
                          <div className="flex-1 min-w-0">
                            <div className="serif text-[15px] text-foreground truncate">
                              {r.name || "Reader"}
                            </div>
                            <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 truncate">
                              {r.handle ? `@${r.handle}` : r.user_id}
                              {r.allow_dms === false && (
                                <span className="ml-2 text-[hsl(var(--destructive))]">· closed inbox</span>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {loadingList ? (
            <div className="py-10 text-center serif italic text-muted-foreground">Loading…</div>
          ) : conversations.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <MessageSquare size={22} className="mx-auto text-muted-foreground/60 mb-3" strokeWidth={1.5} />
              <p className="serif italic text-muted-foreground text-[15px] mb-4">
                No conversations yet.
              </p>
              {!composeOpen && (
                <button
                  type="button"
                  data-testid="compose-empty-cta"
                  onClick={openCompose}
                  className="btn-gold inline-flex items-center gap-2"
                >
                  <Plus size={14} /> Start a conversation
                </button>
              )}
            </div>
          ) : (
            <ul>
              {conversations.map((c) => {
                const slug = c.other.handle || c.other.user_id;
                const active = handleOrId && (handleOrId === c.other.handle || handleOrId === c.other.user_id);
                return (
                  <li key={c.conversation_id}>
                    <Link
                      to={`/messages/${slug}`}
                      data-testid={`convo-${slug}`}
                      className={`flex items-start gap-3 px-5 py-4 hairline-b transition-colors ${
                        active
                          ? "bg-[hsl(var(--primary)/0.06)]"
                          : "hover:bg-[hsl(var(--accent)/0.06)]"
                      }`}
                    >
                      <Avatar picture={c.other.picture} name={c.other.name} size={40} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="serif text-[16px] text-foreground truncate">
                            {c.other.name || "Reader"}
                          </span>
                          <span className="mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 flex-shrink-0">
                            {fmt(c.last_message?.created_at)}
                          </span>
                        </div>
                        {c.other.handle && (
                          <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-1">
                            @{c.other.handle}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <p className={`text-[13px] truncate ${c.unread > 0 ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                            {c.last_message?.mine ? <span className="text-muted-foreground/60">You: </span> : null}
                            {c.last_message?.content || ""}
                          </p>
                          {c.unread > 0 && (
                            <span
                              data-testid={`convo-unread-${slug}`}
                              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 mono text-[10px] tabular-nums bg-[hsl(var(--primary))] text-background rounded-full"
                            >
                              {c.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* THREAD PANE */}
        <section
          data-testid="thread-pane"
          className={`bg-card flex flex-col ${showThreadOnMobile ? "block" : "hidden md:flex"}`}
        >
          {!handleOrId ? (
            <div className="m-auto px-6 py-20 text-center">
              <MessageSquare size={28} className="mx-auto text-muted-foreground/60 mb-3" strokeWidth={1.5} />
              <p className="serif italic text-muted-foreground text-lg mb-5">
                Pick a conversation, open one from a reader&apos;s post, or start a new one.
              </p>
              <button
                type="button"
                data-testid="thread-empty-compose"
                onClick={openCompose}
                className="btn-gold inline-flex items-center gap-2"
              >
                <Plus size={14} /> New message
              </button>
            </div>
          ) : threadLoading || !thread ? (
            <div className="m-auto py-20 serif italic text-muted-foreground">Opening the thread…</div>
          ) : (
            <>
              {/* HEADER */}
              <header className="hairline-b px-5 py-4 flex items-center gap-3">
                <Link
                  to="/messages"
                  data-testid="thread-back"
                  className="md:hidden p-1 -ml-1 text-muted-foreground hover:text-foreground"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft size={18} />
                </Link>
                <Link
                  to={`/u/${thread.other.handle || thread.other.user_id}`}
                  data-testid="thread-other-link"
                  className="flex items-center gap-3 group min-w-0"
                >
                  <Avatar picture={thread.other.picture} name={thread.other.name} size={36} />
                  <div className="min-w-0">
                    <div className="serif text-[17px] text-foreground truncate group-hover:text-[hsl(var(--primary))]">
                      {thread.other.name || "Reader"}
                    </div>
                    {thread.other.handle && (
                      <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
                        @{thread.other.handle}
                      </div>
                    )}
                  </div>
                </Link>
              </header>

              {/* MESSAGES */}
              <div
                data-testid="thread-messages"
                className="flex-1 overflow-y-auto px-5 py-6 space-y-3"
                style={{ maxHeight: "calc(100vh - 280px)" }}
              >
                {(thread.messages || []).length === 0 ? (
                  <p className="text-center serif italic text-muted-foreground py-12">
                    No messages yet. Open with a kind line.
                  </p>
                ) : (
                  (thread.messages || []).map((m) => (
                    <div
                      key={m.id}
                      data-testid={`msg-${m.id}`}
                      className={`flex ${m.mine ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-2.5 rounded-2xl whitespace-pre-wrap break-words text-[15px] leading-relaxed ${
                          m.mine
                            ? "bg-[hsl(var(--primary))] text-background rounded-br-sm"
                            : "bg-[hsl(var(--accent)/0.15)] text-foreground rounded-bl-sm"
                        } ${m.pending ? "opacity-60" : ""}`}
                      >
                        {m.content}
                        <div className={`mono text-[9px] uppercase tracking-[0.18em] mt-1.5 ${m.mine ? "text-background/75" : "text-muted-foreground/80"}`}>
                          {fmt(m.created_at)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={threadEndRef} />
              </div>

              {/* COMPOSER */}
              {thread.other.allow_dms === false ? (
                <div className="hairline-t px-5 py-4 text-center mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground" data-testid="dm-blocked-notice">
                  This reader isn&apos;t accepting messages right now.
                </div>
              ) : (
                <form
                  onSubmit={sendMessage}
                  data-testid="thread-composer"
                  className="hairline-t px-4 py-3 flex items-end gap-2"
                >
                  <textarea
                    data-testid="thread-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Write quietly…"
                    rows={1}
                    maxLength={2000}
                    className="flex-1 input-line resize-none text-[15px] leading-relaxed max-h-32 min-h-[40px] py-2"
                  />
                  <button
                    type="submit"
                    data-testid="thread-send"
                    disabled={sending || !draft.trim()}
                    aria-label="Send"
                    className="btn-gold inline-flex items-center justify-center w-12 h-10 px-0 disabled:opacity-40"
                  >
                    <Send size={16} strokeWidth={2} />
                  </button>
                </form>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
